import { init, captureException, setUser, browserTracingIntegration, globalHandlersIntegration, captureConsoleIntegration } from '@sentry/browser';

const system = SYSTEM;
const ENV_NAME = IS_ONLINE ? 'pro' : IS_PRODUCTION ? 'dev' : 'local';

const tracesSampleRateMap = {
    CN: 0.5,
    TW: 1,
    JP: 0.5,
    EN: 1,
    EM: 1,
};

// ==================== 网络质量检测工具 ====================

interface NetworkInfo {
    effectiveType: string;
    downlink: number;
    rtt: number;
    apiAvailable: boolean;
}

function estimateFromTiming(): { rtt: number; downlink: number } {
    try {
        if (typeof performance === 'undefined') return { rtt: -1, downlink: -1 };
        const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        if (!navEntries.length) return { rtt: -1, downlink: -1 };
        const nav = navEntries[0];
        let rtt = nav.connectEnd - nav.connectStart;
        if (rtt <= 0) rtt = nav.responseStart - nav.requestStart;
        let downlink = -1;
        const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        for (let i = 0; i < resources.length; i++) {
            const entry = resources[i];
            const dur = entry.responseEnd - entry.responseStart;
            if (entry.transferSize > 0 && dur > 10) {
                const tp = (entry.transferSize * 8) / (dur * 1000);
                if (tp > downlink) downlink = tp;
            }
        }
        const navDur = nav.responseEnd - nav.responseStart;
        if (nav.transferSize > 0 && navDur > 10) {
            const tp = (nav.transferSize * 8) / (navDur * 1000);
            if (tp > downlink) downlink = tp;
        }
        return { rtt: rtt > 0 ? rtt : -1, downlink };
    } catch (_) {
        return { rtt: -1, downlink: -1 };
    }
}

function getNetworkInfo(): NetworkInfo {
    const nav = navigator as any;
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
    if (conn) {
        const rtt = typeof conn.rtt === 'number' ? conn.rtt : -1;
        const downlink = typeof conn.downlink === 'number' ? conn.downlink : -1;
        if (rtt !== 0 || downlink !== 0) {
            return { effectiveType: conn.effectiveType || 'unknown', downlink, rtt, apiAvailable: true };
        }
    }
    const timing = estimateFromTiming();
    return {
        effectiveType: (conn && conn.effectiveType) || 'unknown',
        downlink: timing.downlink,
        rtt: timing.rtt,
        apiAvailable: false,
    };
}

function isWeakNetwork(): boolean {
    const { effectiveType, rtt, downlink } = getNetworkInfo();
    if (['2g', 'slow-2g', '3g'].indexOf(effectiveType) !== -1) return true;
    if (rtt > 500) return true;
    if (downlink > 0 && downlink < 1) return true;
    return false;
}

function getNavigationTiming(): { connectTime: number; dnsTime: number } {
    try {
        if (typeof performance === 'undefined') return { connectTime: -1, dnsTime: -1 };
        const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        if (!navEntries.length) return { connectTime: -1, dnsTime: -1 };
        const nav = navEntries[0];
        return {
            connectTime: nav.connectEnd - nav.connectStart,
            dnsTime: nav.domainLookupEnd - nav.domainLookupStart,
        };
    } catch (_) {
        return { connectTime: -1, dnsTime: -1 };
    }
}

function attachNetworkTags(event: any): void {
    const info = getNetworkInfo();
    if (!event.tags) event.tags = {};
    event.tags['network.effectiveType'] = info.effectiveType;
    event.tags['network.downlink'] = String(info.downlink);
    event.tags['network.rtt'] = String(info.rtt);
    event.tags['network.isWeak'] = String(isWeakNetwork());
    event.tags['network.apiAvailable'] = String(info.apiAvailable);
}

// ==================== 爬虫/Bot 流量检测 ====================

const BOT_PATTERNS = [/bot/i, /crawler/i, /spider/i, /headless/i, /puppeteer/i, /playwright/i, /phantom/i, /selenium/i, /scrapy/i, /yandex/i, /Sogou/i, /facebookexternalhit/i, /Slurp/i];

function isBotTraffic(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return BOT_PATTERNS.some((pattern) => pattern.test(ua));
}

let _lastHiddenTimestamp = 0;
if (typeof document !== 'undefined') {
    if (document.visibilityState === 'hidden') _lastHiddenTimestamp = Date.now();
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') _lastHiddenTimestamp = Date.now();
    });
}

function getSentryConfig() {
    const SENTRY_CONFIG = {
        CN: {
            dsn: 'https://32ba5348016b4f5995ce5f952b3caf47@sentry.umu.cn/15',
        },
        TW: {
            dsn: 'https://92483313a7334bca8e98aa0260c1449a@sentry.umu.tw/16',
        },
        JP: {
            dsn: 'https://f8f5424567ff44a693b3f28fd1495481@sentry.umu.co/18',
        },
        EN: {
            dsn: 'https://8501b77f52624460b9f26f125381284e@sentry.umu.com/17',
        },
        EM: {
            dsn: 'https://2ee9d75299d841aeb6e44ccd0d6238ef@sentry.umu.com/18',
        },
    };
    const config = SENTRY_CONFIG[system.toUpperCase() as keyof typeof SENTRY_CONFIG];
    return {
        sentryDSN: config.dsn,
        tracesSampleRate: tracesSampleRateMap[system.toUpperCase() as keyof typeof tracesSampleRateMap],
        release: window.VERSION,
    };
}

function registerSentry() {
    if (ENV_NAME !== 'pro') {
        return;
    }
    const { sentryDSN, tracesSampleRate, release } = getSentryConfig();

    if (!sentryDSN) {
        return;
    }

    init({
        environment: ENV_NAME,
        dsn: sentryDSN,
        integrations: [
            browserTracingIntegration(),
            globalHandlersIntegration({
                onunhandledrejection: true,
                onerror: true,
            }),
            captureConsoleIntegration({ levels: ['error'] }),
        ],
        ignoreErrors: [
            '[video-takeover-extract]',
            'empty host rule',
            "Can't find variable: WebViewJavascriptBridge",
            'WebViewJavascriptBridge is not defined',
            'AbortError: The operation was aborted.',
            'Load failed',
            'TypeError: Load failed',
            'Failed to fetch',
            'TypeError: Failed to fetch',
            'No error message',
            'The network connection was lost.',
            "Can't find variable: cordova",
            'appcb is not defined',
            '網路連線中斷。',
            '已取消',
            'Internet連線已斷開。',
            'LanxinJsBridge is not defined',
            "Can't find variable: LanxinJsBridge",
            "Can't find variable: ResizeObserver",
            'window.android.unLoad is not a function',
            /^AbortError: The play/i,
            /^NotAllowedError: The request is not/i,
            /^The fetching process for the media resource was aborted/i,
            // 浏览器插件注入错误，非业务代码问题
            'Invalid call to runtime.sendMessage()',
            "Can't find variable: observer",
            // 脚本传输过程中损坏导致语法错误，非代码逻辑问题
            'Return statements are only valid inside functions',
            // npm 包 @umu/ukit-lang-npm 的 i18n 请求在网络异常时 JSON.parse 失败，
            // 包内已有 try-catch 兜底，但 console.error 被 captureConsoleIntegration 捕获上报
            /^SyntaxError: Unexpected end of JSON input$/,
            /^SyntaxError: JSON Parse error: Unexpected EOF$/,
        ],
        denyUrls: [/^chrome:/i, /chrome-extension:/i, /^file:/i],
        // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
        // We recommend adjusting this value in production
        release,
        maxBreadcrumbs: 50,
        tracesSampleRate: tracesSampleRate,
        // https://docs.sentry.io/platforms/javascript/configuration/filtering/
        beforeSend: (event: any, _hint: any) => {
            // 用户将网页保存到本地桌面后通过 file:// 打开，API 全部失败产生无效错误
            if (window.location.protocol === 'file:') return null;
            // 为错误事件附加网络质量标签
            attachNetworkTags(event);
            // 过滤爬虫/Bot 流量的错误事件
            if (isBotTraffic()) return null;
            // filter out UnhandledRejection errors that have no information https://github.com/getsentry/sentry-javascript/issues/3440
            const ignoreErrorList = ['Please use the NodeViewWrapper component for your node view.', "'get' on proxy: property 'javaEnabled' is a read-only", 'Loading chunk 1 failed.', "Can't find variable: Aplus4Native"];
            if (event !== undefined && event.exception !== undefined && event.exception.values !== undefined && event.exception.values.length === 1) {
                const e = event.exception.values[0] || {};
                const exceptionMessage = e.value;
                if (e.type === 'UnhandledRejection' && exceptionMessage && exceptionMessage.indexOf('Non-Error promise rejection captured with value:') > -1) {
                    return null;
                }
                // 忽略 [object Event] 错误
                if (e.type === 'Event') {
                    return null;
                }
                // 忽略一些错误
                if (ignoreErrorList.some((error) => exceptionMessage && exceptionMessage.indexOf(error) > -1)) {
                    return null;
                }
            }
            return event;
        },
        // 过滤弱网环境 & 爬虫流量的性能事务数据，避免脏数据影响 P75 指标
        beforeSendTransaction: (event: any) => {
            attachNetworkTags(event);
            if (!event.tags) event.tags = {};
            const txStartMs = (event.start_timestamp || 0) * 1000;
            const wasHiddenDuringTx = _lastHiddenTimestamp > 0 && _lastHiddenTimestamp >= txStartMs;
            const ttfb = event.measurements?.ttfb?.value;
            const navTiming = getNavigationTiming();
            event.tags['page.wasBackgrounded'] = String(wasHiddenDuringTx);
            event.tags['network.connectTime'] = String(navTiming.connectTime);
            event.tags['network.dnsTime'] = String(navTiming.dnsTime);
            if (isBotTraffic()) return null;
            if (wasHiddenDuringTx || (typeof ttfb === 'number' && ttfb > 10000)) return null;
            if (isWeakNetwork()) return null;
            if (navTiming.connectTime > 3000 || navTiming.dnsTime > 3000) return null;
            // 兜底：前置过滤均通过但 LCP 仍超 15s，视为非正常加载（CDN 故障、浏览器冻结等）
            // 兼容 Sentry SDK v7（key: 'largest-contentful-paint'）和 v8（key: 'lcp'）两种命名
            const lcpMeasurement = event.measurements?.['lcp'] ?? event.measurements?.['largest-contentful-paint'];
            const lcp = lcpMeasurement?.value;
            if (typeof lcp === 'number' && lcp > 15000) return null;
            return event;
        },
    });
    window.Sentry = { init, captureException, setUser };
    const userInfo = (window.pageData.data && (window.pageData.data.teacher_info || window.pageData.data.user_info)) || {};
    const params: any = {};
    if (userInfo.enterpriseId) {
        params.eId = userInfo.enterpriseId;
    }
    if (userInfo.umu_id) {
        params.uId = userInfo.umu_id;
    }
    if (userInfo.userName) {
        params.username = userInfo.userName;
    }
    if (userInfo.email) {
        params.email = userInfo.email;
    }
    setUser(params);
}

export default registerSentry;
